import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Position } from '../types';
import { PositionService } from '../services/positionService';
import { formatCurrency, formatPercent } from '../utils/formatters';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface EditPositionDialogProps {
  open: boolean;
  onClose: () => void;
  position: Position | null;
  onPositionUpdated: () => void;
}

const EditPositionDialog: React.FC<EditPositionDialogProps> = ({
  open,
  onClose,
  position,
  onPositionUpdated,
}) => {
  const [stopLoss, setStopLoss] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [entryDate, setEntryDate] = useState<Date | null>(null);
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPriceChangeWarning, setShowPriceChangeWarning] = useState(false);

  useEffect(() => {
    if (position) {
      setStopLoss(position.stopLoss.toFixed(2));
      setTargetPrice(position.targetPrice.toFixed(2));
      setEntryPrice(position.entryPrice.toFixed(2));
      setEntryDate(new Date(position.entryDate));
      setQuantity(position.quantity.toString());
      setShowPriceChangeWarning(false);
    }
  }, [position]);

  useEffect(() => {
    if (position && (entryPrice || quantity)) {
      const newEntryPrice = parseFloat(entryPrice);
      const newQuantity = parseInt(quantity);
      if ((!isNaN(newEntryPrice) && newEntryPrice !== position.entryPrice) || 
          (!isNaN(newQuantity) && newQuantity !== position.quantity)) {
        setShowPriceChangeWarning(true);
      } else {
        setShowPriceChangeWarning(false);
      }
    }
  }, [entryPrice, quantity, position]);

  const handleClose = () => {
    setStopLoss('');
    setTargetPrice('');
    setEntryPrice('');
    setEntryDate(null);
    setQuantity('');
    setError(null);
    setShowPriceChangeWarning(false);
    onClose();
  };

  const handleUpdatePosition = async () => {
    if (!position || !entryDate) return;

    setError(null);
    setLoading(true);

    try {
      const newStopLoss = parseFloat(stopLoss);
      const newTargetPrice = parseFloat(targetPrice);
      const newEntryPrice = parseFloat(entryPrice);
      const newQuantity = parseInt(quantity);

      if (isNaN(newStopLoss) || newStopLoss <= 0) {
        throw new Error('Please enter a valid stop loss price');
      }

      if (isNaN(newTargetPrice) || newTargetPrice <= 0) {
        throw new Error('Please enter a valid target price');
      }

      if (isNaN(newEntryPrice) || newEntryPrice <= 0) {
        throw new Error('Please enter a valid entry price');
      }

      if (isNaN(newQuantity) || newQuantity <= 0) {
        throw new Error('Please enter a valid quantity');
      }

      // Validate entry date is not in the future
      if (entryDate > new Date()) {
        throw new Error('Entry date cannot be in the future');
      }

      if (newStopLoss >= newEntryPrice) {
        throw new Error('Stop loss must be below entry price');
      }

      if (newTargetPrice <= newEntryPrice) {
        throw new Error('Target price must be above entry price');
      }

      await PositionService.updatePositionDetails(
        position.id,
        newStopLoss,
        newTargetPrice,
        newEntryPrice,
        entryDate,
        newQuantity
      );

      onPositionUpdated();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update position');
    } finally {
      setLoading(false);
    }
  };

  if (!position) return null;

  const stopLossNum = parseFloat(stopLoss) || 0;
  const targetPriceNum = parseFloat(targetPrice) || 0;
  const entryPriceNum = parseFloat(entryPrice) || position.entryPrice;
  const quantityNum = parseInt(quantity) || position.quantity;
  
  const stopLossPercent = entryPriceNum > 0 
    ? ((stopLossNum - entryPriceNum) / entryPriceNum) * 100 
    : 0;
  
  const targetPercent = entryPriceNum > 0 
    ? ((targetPriceNum - entryPriceNum) / entryPriceNum) * 100 
    : 0;

  const riskAmount = (entryPriceNum - stopLossNum) * quantityNum;
  const rewardAmount = (targetPriceNum - entryPriceNum) * quantityNum;
  const riskRewardRatio = riskAmount > 0 ? rewardAmount / riskAmount : 0;

  // Calculate P&L impact of entry price and quantity changes
  const originalPL = (position.currentPrice - position.entryPrice) * position.quantity;
  const newPL = (position.currentPrice - entryPriceNum) * quantityNum;
  const plDifference = newPL - originalPL;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Position - {position.symbol}
          <Typography variant="body2" color="textSecondary">
            {quantityNum !== position.quantity ? (
              <>
                <span style={{ textDecoration: 'line-through' }}>{position.quantity}</span> → {quantityNum}
              </>
            ) : position.quantity} shares @ {entryPriceNum !== position.entryPrice ? (
              <>
                <span style={{ textDecoration: 'line-through' }}>{formatCurrency(position.entryPrice)}</span> → {formatCurrency(entryPriceNum)}
              </>
            ) : formatCurrency(position.entryPrice)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={3}>
              <Typography variant="subtitle2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Entry Details
                <Tooltip title="Adjust entry price and date to correct any discrepancies from quick execution or broker issues">
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
              </Typography>

              <Box>
                <TextField
                  fullWidth
                  label="Entry Price"
                  type="number"
                  value={entryPrice}
                  onChange={(e) => setEntryPrice(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { step: 0.01, min: 0 },
                  }}
                  helperText="Actual price paid per share"
                />
              </Box>

              <Box>
                <DateTimePicker
                  label="Entry Date & Time"
                  value={entryDate}
                  onChange={(newDate: Date | null) => newDate && setEntryDate(newDate)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: "When the position was actually opened"
                    }
                  }}
                  maxDate={new Date()}
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  InputProps={{
                    inputProps: { step: 1, min: 1 },
                  }}
                  helperText="Number of shares"
                />
              </Box>

              {showPriceChangeWarning && (
                <Alert severity="warning" icon={<WarningAmberIcon />}>
                  <Typography variant="body2">
                    Changing the entry price or quantity will affect your P&L calculations.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Current P&L: {formatCurrency(originalPL)} → New P&L: {formatCurrency(newPL)}
                    <br />
                    <strong>Impact: {plDifference >= 0 ? '+' : ''}{formatCurrency(plDifference)}</strong>
                  </Typography>
                </Alert>
              )}

              <Divider />

              <Typography variant="subtitle2" color="primary">
                Target & Stop Loss
              </Typography>

              <Box>
                <TextField
                  fullWidth
                  label="Stop Loss Price"
                  type="number"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { step: 0.01, min: 0 },
                    endAdornment: stopLossNum > 0 && (
                      <Chip 
                        label={`${stopLossPercent >= 0 ? '+' : ''}${stopLossPercent.toFixed(2)}%`}
                        size="small"
                        color={stopLossPercent < 0 ? 'error' : 'default'}
                      />
                    ),
                  }}
                  helperText="Price at which to sell if the stock drops"
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label="Target Price"
                  type="number"
                  value={targetPrice}
                  onChange={(e) => setTargetPrice(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { step: 0.01, min: 0 },
                    endAdornment: targetPriceNum > 0 && (
                      <Chip 
                        label={`${targetPercent >= 0 ? '+' : ''}${targetPercent.toFixed(2)}%`}
                        size="small"
                        color={targetPercent > 0 ? 'success' : 'default'}
                      />
                    ),
                  }}
                  helperText="Price at which to take profits"
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Risk/Reward Analysis
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                      Risk (to stop loss):
                    </Typography>
                    <Typography variant="body2" color="error">
                      {formatCurrency(riskAmount)} ({formatPercent(Math.abs(stopLossPercent))})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                      Reward (to target):
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      {formatCurrency(rewardAmount)} ({formatPercent(targetPercent)})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                      Risk/Reward Ratio:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={riskRewardRatio >= 2 ? 'success.main' : riskRewardRatio >= 1 ? 'warning.main' : 'error.main'}
                    >
                      1:{riskRewardRatio.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  <strong>Current Price:</strong> {formatCurrency(position.currentPrice)}
                  <br />
                  <strong>Unrealized P&L:</strong> {' '}
                  <span style={{ color: newPL >= 0 ? '#2e7d32' : '#d32f2f' }}>
                    {newPL >= 0 ? '+' : ''}
                    {formatCurrency(newPL)}
                  </span>
                  {showPriceChangeWarning && (
                    <>
                      <br />
                      <strong>Original P&L:</strong> {' '}
                      <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
                        {originalPL >= 0 ? '+' : ''}
                        {formatCurrency(originalPL)}
                      </span>
                    </>
                  )}
                </Typography>
              </Alert>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdatePosition}
            disabled={loading || !stopLoss || !targetPrice || !entryPrice || !entryDate}
          >
            Update Position
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EditPositionDialog;